import {
	BOT_COMMAND_PATTERN,
	isEmptyTriggers,
	type KeywordMatch,
	parseTriggers,
} from "@velizhanin-dev/bot-scenario"
import { KeyRound, Plus, Trash2 } from "lucide-react"
import { useState } from "react"
import { Button } from "@/shared/ui/button"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/shared/ui/dialog"
import { EmptyState } from "@/shared/ui/empty-state"
import { Field } from "@/shared/ui/form-field"
import { Input } from "@/shared/ui/input"
import { NativeSelect } from "@/shared/ui/native-select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table"
import { type LoadedDraft, WithDraft, WorkspaceSection } from "@/widgets/bot-workspace"
import { AutoHeight } from "@/shared/ui/auto-height"
import { LoadSwap } from "@/shared/ui/load-swap"

const SUBTITLE =
	"Слово или команда в сообщении запускает воронку из любого места диалога. Например, на «стоимость» бот пришлёт прайс."

type Row =
	| { kind: "keyword"; flowId: string; text: string; match: KeywordMatch }
	| { kind: "command"; flowId: string; command: string; description?: string }

const MATCH_LABELS: Record<KeywordMatch, string> = {
	exact: "точно совпадает",
	contains: "содержит",
}

/** «Конструктор → Ключевые слова»: все слова и команды воронок одним списком. */
export function KeywordsPage() {
	return (
		<WithDraft title="Ключевые слова" subtitle={SUBTITLE}>
			{(state) => <KeywordsTable state={state} />}
		</WithDraft>
	)
}

function KeywordsTable({ state }: { state: LoadedDraft }) {
	const { draft, actions } = state
	const flows = draft.flows.filter((flow) => flow.kind !== "FALLBACK")
	const [adding, setAdding] = useState(false)
	const [form, setForm] = useState({
		kind: "keyword" as Row["kind"],
		text: "",
		match: "contains" as KeywordMatch,
		flowId: flows[0]?.id ?? "",
	})

	const rows: Row[] = draft.flows.flatMap((flow) => {
		const triggers = parseTriggers(flow.triggers)
		return [
			...triggers.keywords.map(
				(keyword): Row => ({ kind: "keyword", flowId: flow.id, ...keyword }),
			),
			...triggers.commands.map(
				(command): Row => ({ kind: "command", flowId: flow.id, ...command }),
			),
		]
	})

	const save = async (
		flowId: string,
		change: (triggers: ReturnType<typeof parseTriggers>) => void,
	) => {
		const flow = draft.flows.find((item) => item.id === flowId)
		if (!flow) return
		const triggers = parseTriggers(flow.triggers)
		change(triggers)
		await actions.setFlowTriggers(flowId, isEmptyTriggers(triggers) ? null : triggers)
	}

	const text = form.text.trim()
	const valid =
		!!form.flowId && (form.kind === "command" ? BOT_COMMAND_PATTERN.test(text) : text.length > 0)

	return (
		<WorkspaceSection
			title="Ключевые слова"
			subtitle={SUBTITLE}
			actions={
				<Button
					className="rounded-xl"
					onClick={() => setAdding(true)}
					disabled={flows.length === 0}
				>
					<Plus />
					Добавить ключевое слово
				</Button>
			}
		>
			{/* «Пусто» и список сменяют друг друга перекрёстно, высота тянется к новой. */}
			<LoadSwap loading={false} id={rows.length === 0 ? "empty" : "list"}>
				{rows.length === 0 ? (
					<EmptyState
						icon={KeyRound}
						title="Ключевых слов пока нет"
						action={
							flows.length > 0 ? (
								<Button className="rounded-xl" onClick={() => setAdding(true)}>
									<Plus />
									Добавить ключевое слово
								</Button>
							) : undefined
						}
					>
						Например, человек пишет «цена», а бот в ответ запускает нужную воронку.
					</EmptyState>
				) : (
					<AutoHeight className="overflow-x-auto rounded-2xl border border-border bg-card">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Слово или команда</TableHead>
									<TableHead>Как сравнивать</TableHead>
									<TableHead>Воронка</TableHead>
									<TableHead className="w-10" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{rows.map((row) => (
									<TableRow
										key={`${row.flowId}-${row.kind}-${row.kind === "keyword" ? `${row.match}-${row.text}` : row.command}`}
									>
										<TableCell className="font-medium">
											{row.kind === "keyword" ? row.text : `/${row.command}`}
										</TableCell>
										<TableCell className="text-muted-foreground">
											{row.kind === "keyword" ? MATCH_LABELS[row.match] : "команда"}
										</TableCell>
										<TableCell>
											{draft.flows.find((flow) => flow.id === row.flowId)?.name}
										</TableCell>
										<TableCell>
											<Button
												variant="ghost"
												size="icon"
												aria-label="Удалить"
												onClick={() =>
													void save(row.flowId, (triggers) => {
														if (row.kind === "keyword")
															triggers.keywords = triggers.keywords.filter(
																(item) => !(item.text === row.text && item.match === row.match),
															)
														else
															triggers.commands = triggers.commands.filter(
																(item) => item.command !== row.command,
															)
													})
												}
											>
												<Trash2 />
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</AutoHeight>
				)}
			</LoadSwap>

			<Dialog open={adding} onOpenChange={setAdding}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Новое ключевое слово</DialogTitle>
						<DialogDescription>
							«Содержит» не перебивает ответ на вопрос воронки: сработает, только когда бот не ждёт
							ответа.
						</DialogDescription>
					</DialogHeader>
					<form
						className="space-y-4"
						onSubmit={(event) => {
							event.preventDefault()
							if (!valid) return
							void save(form.flowId, (triggers) => {
								if (form.kind === "command") triggers.commands.push({ command: text.toLowerCase() })
								else triggers.keywords.push({ text, match: form.match })
							}).then(() => {
								setAdding(false)
								setForm({ ...form, text: "" })
							})
						}}
					>
						<Field label="Что это" htmlFor="keyword-kind">
							<NativeSelect
								id="keyword-kind"
								value={form.kind}
								onChange={(event) => setForm({ ...form, kind: event.target.value as Row["kind"] })}
							>
								<option value="keyword">Слово в сообщении</option>
								<option value="command">Команда /…</option>
							</NativeSelect>
						</Field>
						<Field
							label={form.kind === "command" ? "Команда без /" : "Слово"}
							htmlFor="keyword-text"
							hint={
								form.kind === "command" && text && !BOT_COMMAND_PATTERN.test(text)
									? "Латиница в нижнем регистре, цифры и _"
									: undefined
							}
						>
							<Input
								id="keyword-text"
								placeholder={form.kind === "command" ? "Например, price" : "Например, цена"}
								maxLength={64}
								value={form.text}
								onChange={(event) => setForm({ ...form, text: event.target.value })}
							/>
						</Field>
						{form.kind === "keyword" ? (
							<Field label="Как сравнивать" htmlFor="keyword-match">
								<NativeSelect
									id="keyword-match"
									value={form.match}
									onChange={(event) =>
										setForm({ ...form, match: event.target.value as KeywordMatch })
									}
								>
									<option value="contains">содержит</option>
									<option value="exact">точно совпадает</option>
								</NativeSelect>
							</Field>
						) : null}
						<Field label="Какую воронку запустить" htmlFor="keyword-flow">
							<NativeSelect
								id="keyword-flow"
								value={form.flowId}
								onChange={(event) => setForm({ ...form, flowId: event.target.value })}
							>
								{flows.map((flow) => (
									<option key={flow.id} value={flow.id}>
										{flow.name}
									</option>
								))}
							</NativeSelect>
						</Field>
						<DialogFooter>
							<Button type="submit" disabled={!valid}>
								Добавить
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</WorkspaceSection>
	)
}
